/*======================================================================================================================
  * Bluebell: src/app/bramble/runs/runs-service/runs.service.ts
  * Last Edited: 30/06/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable } from '@angular/core';
import {
  Case,
  FullRun,
  RunCFDSetupErrors,
} from '@state/runs/run.interfaces';


export type RunPostproTypeItem = 'plot'|'movie'|'check'|'annotation'|'image_delta_plot';
export type RunPostproType = RunPostproTypeItem
  | `${RunPostproTypeItem},${RunPostproTypeItem}`
  | `${RunPostproTypeItem},${RunPostproTypeItem},${RunPostproTypeItem}`
  | `${RunPostproTypeItem},${RunPostproTypeItem},${RunPostproTypeItem},${RunPostproTypeItem}`
  | `${RunPostproTypeItem},${RunPostproTypeItem},${RunPostproTypeItem},${RunPostproTypeItem},${RunPostproTypeItem}`;

export interface RunFullName {
  full_name: string;
}

export const RUN_PAGINATION_STORAGE_KEY = 'RunsTableState';

@Injectable({
  providedIn: 'root'
})
export class RunsService {
  /**
   * Checks the run for any errors when a new run is selected or reloaded.
   */
  checkForCFDSetupErrors(run: FullRun|null|undefined): RunCFDSetupErrors|null {
    if (run) {
      let errors: RunCFDSetupErrors = {};
      run.cases.forEach(_case => {
        // we currently have only one check for generate_underfloor
        // if "generate underfloor" case exists
        if (_case.cfd_settings.generate_underfloor.length) {
          const partsWithBodyZones = [];
          const partsWithUnderfloorZone = [];
          // check for any errors in the model build for a "generate underfloor" case
          run.parts.forEach(assembly => {
            assembly.tri_surfaces.forEach(part => {
              // Error 1 - multiple parts with body.* zones
              if (part.zones.some(zone => zone.match('body.*'))) {
                partsWithBodyZones.push(part);
              }
              // Error 2 - uf zone already exists
              if (part.zones.includes('uf')) {
                partsWithUnderfloorZone.push(part);
              }
            });
          });

          if (partsWithBodyZones.length !== 1 || partsWithUnderfloorZone.length !== 0) {
            // append to errors object - gets passed to run summary / cfd setup components to display error messages
            errors = {
              ...errors,
              [_case.uuid]: _case.uuid in errors
                ? [...errors[_case.uuid], { type: 'generate_underfloor_error', message: '(Errors found in model build)' }]
                : [{ type: 'generate_underfloor_error', message: '(Errors found in model build)' }],
            };
          }
        }

        if (_case.cfd_settings.workflow.name.toLowerCase().includes('donor')) {
          const donor_cases = run.cases.filter(c => !c.cfd_settings.workflow.name.toLowerCase().includes('donor'));
          // check if no donor cases exists
          if ((
            !donor_cases.length
          )) {
            errors = {
              ...errors,
              [_case.uuid]: _case.uuid in errors
                ? [...errors[_case.uuid], { type: 'attitude_error', message: '(Could not find a donor case)' }]
                : [{ type: 'attitude_error', message: '(Could not find a donor case)' }],
            };
          }
          // If the attitude name follows a '<donor_attitude_name>__DES' or similar structure, check the
          // <donor_attitude_name> exists on the run.
          const splitAttitudeName = _case.name.split('__');
          const matchedDonorCase = donor_cases.find(c => c.name === splitAttitudeName[0]);
          // Check if more than 1 donor case exists
          if ((
            donor_cases.length > 1
          )) {
            // If a matched donor case exists on the run, pass without error, else return the standard
            // error for more than 1 donor case
            if (!matchedDonorCase) {
              errors = {
                ...errors,
                [_case.uuid]: _case.uuid in errors
                  ? [...errors[_case.uuid], { type: 'attitude_error', message: '(More than 1 donor case exist)' }]
                  : [{ type: 'attitude_error', message: '(More than 1 donor case exist)' }],
                };
            }
          }
          // check if a queue mismatch between recipient and donor exists
          if (donor_cases.length === 1 || (
            donor_cases.length > 1 && !!matchedDonorCase
          )) {
            const donor_case = donor_cases.length > 1 ? matchedDonorCase : donor_cases[0];
            if (_case.cfd_settings.queue !== donor_case?.cfd_settings.queue) {
              errors = {
                ...errors,
                [_case.uuid]: _case.uuid in errors
                  ? [...errors[_case.uuid], { type: 'queue_error', message: '(Queue mismatch between donor/recipient cases)' }]
                  : [{ type: 'queue_error', message: '(Queue mismatch between donor/recipient cases)' }],
              };
            }
          }
        }
        if (_case.cfd_settings.workflow.name.toLowerCase().includes('mapped inlet')) {
          // check a mapped inlet source has been selected
          if (
            !Object.hasOwn(_case.metadata, 'mapped_inlet_source')
            || (
              Object.hasOwn(_case.metadata, 'mapped_inlet_source') && !_case.metadata.mapped_inlet_source
            )
          ) {
            errors = {
              ...errors,
              [_case.uuid]: _case.uuid in errors
                ? [...errors[_case.uuid], { type: 'mapped_inlet_error', message: '(A mapped inlet source has not been selected)' }]
                : [{ type: 'mapped_inlet_error', message: '(A mapped inlet source has not been selected)' }],
            };
          }
        }
      });
    }
    return null;
  }

  checkDonorRecipient(_case: Case, cases: Case[]): boolean {
    let disableTransition = false;
    // special case for donor/recipient workflow
    const donor_cases = cases.filter(c => !c.cfd_settings.workflow.name.toLowerCase().includes('donor'));

    // If the attitude name follows a '<donor_attitude_name>__DES' or similar structure, check the
    // <donor_attitude_name> exists on the run.
    const splitAttitudeName = _case.name.split('__');
    const matchedDonorCase = donor_cases.find(c => c.name === splitAttitudeName[0]);

    // donor_cases.length should only be greater than 1 if matchedDonorCase exists, as check at launch should prevent
    // the case from reaching this point without a matched donor case
    const donor_case = donor_cases.length > 1 ? matchedDonorCase : donor_cases[0];

    // only apply for Mesh & Solve button
    if (donor_case) {
      // check if recipient case has a donor workflow and pending at Mesh & Solve Stage.
      if (_case.cfd_settings.workflow.name.toLowerCase().includes('donor') &&
          _case.lifecycle.current_action === 'CFD_SOLVE_CASE' &&
          _case.lifecycle.status === 'pending') {

        // check if Mesh & solve stage is not completed for donor case
        if (!donor_case.lifecycle.actions.find(action =>
          action.name === 'CFD_SOLVE_CASE' &&
          action.status === 'completed')) {
          // disable Mesh & Solve
          disableTransition = true;
        }
      }
    }
    return disableTransition;
  }

  clearTableState() {
    sessionStorage.removeItem(RUN_PAGINATION_STORAGE_KEY);
  }
}
